import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, Truck, Package, Clock, Phone, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/seo/cities/${slug}`).then((r) => setCity(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (city) {
      document.title = `Trasporti ${city.name} | MV Trasporti - Spedizioni e Logistica`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", city.description);
    }
  }, [city]);

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF5200] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!city) return (
    <div className="min-h-screen pt-24 text-center py-20">
      <h1 className="text-2xl font-heading font-bold text-[#09090B] mb-4">Citta non trovata</h1>
      <Link to="/blog" className="text-[#FF5200] font-bold text-sm uppercase tracking-widest hover:underline">Vedi tutte le citta</Link>
    </div>
  );

  const services = [
    { icon: Truck, title: `Trasporto Merci ${city.name}`, desc: `Servizio di trasporto merci professionale a ${city.name} e provincia. Ritiri e consegne giornaliere per aziende e privati.` },
    { icon: Package, title: `Spedizioni Pallet ${city.name}`, desc: `Spedizione pallet a ${city.name} con tariffe competitive. Da 60 euro per pallet in Lombardia/Veneto, da 120 euro per il resto d'Italia.` },
    { icon: Clock, title: `Servizio Express ${city.name}`, desc: `Consegne urgenti a ${city.name} con servizio express e dedicato. Ritiro in giornata e consegna rapida garantita.` },
  ];

  const advantages = [
    "Ritiri e consegne giornaliere",
    "Tracking in tempo reale",
    "Preventivo online gratuito",
    "Personale qualificato",
    "Assicurazione merce inclusa",
    "Servizio clienti dedicato",
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="city-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-16">
        <div className="flex items-center gap-2 text-xs text-zinc-400 mb-6 font-body">
          <Link to="/" className="hover:text-[#FF5200]">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-[#FF5200]">Trasporti</Link>
          <span>/</span>
          <span className="text-zinc-600">{city.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} className="text-[#FF5200]" />
              <span className="text-xs font-bold tracking-widest uppercase text-[#FF5200] font-heading">{city.region}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black uppercase leading-[0.9] tracking-tight text-[#09090B] mb-6" data-testid="city-page-title">
              Trasporti<br />
              <span className="text-[#FF5200]">{city.name}</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-600 font-body leading-relaxed mb-8">
              {city.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/preventivo"
                className="inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white px-6 py-3 text-sm font-bold uppercase tracking-widest font-heading hover:bg-[#e64a00] transition-colors"
                data-testid="city-cta-quote"
              >
                Preventivo Gratuito <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+393347708108"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#09090B] text-[#09090B] px-6 py-3 text-sm font-bold uppercase tracking-widest font-heading hover:bg-[#09090B] hover:text-white transition-colors"
                data-testid="city-cta-call"
              >
                <Phone size={16} /> Chiamaci
              </a>
            </div>
          </div>

          {/* Stats box */}
          <div className="bg-[#09090B] p-8 md:p-10 space-y-6">
            <p className="text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">I nostri numeri</p>
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "60€", label: "Pallet locale" },
                { value: "120€", label: "Pallet nazionale" },
                { value: "15€", label: "Min. per collo" },
                { value: "24/48h", label: "Consegna standard" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-3xl font-heading font-black text-[#FF5200]">{stat.value}</p>
                  <p className="text-xs text-zinc-500 font-body mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-16">
        <h2 className="font-heading font-bold text-base md:text-lg uppercase tracking-wide text-[#09090B] mb-8">
          I nostri servizi a {city.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="border border-zinc-200 bg-white p-6 hover:border-[#FF5200] transition-colors">
              <s.icon size={24} className="text-[#FF5200] mb-4" />
              <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-[#09090B] mb-3">{s.title}</h3>
              <p className="text-sm text-zinc-600 font-body leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Advantages */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-16">
        <div className="bg-zinc-50 border border-zinc-200 p-8 md:p-12">
          <h2 className="font-heading font-bold text-base md:text-lg uppercase tracking-wide text-[#09090B] mb-8">
            Perche scegliere MV Trasporti a {city.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {advantages.map((adv) => (
              <div key={adv} className="flex items-center gap-3">
                <CheckCircle size={16} className="text-[#FF5200] flex-shrink-0" />
                <span className="text-sm text-zinc-700 font-body">{adv}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other cities */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="border-t border-zinc-200 pt-12">
          <h2 className="font-heading font-bold text-base md:text-lg uppercase tracking-wide text-[#09090B] mb-6">
            Altre citta servite
          </h2>
          <div className="flex flex-wrap gap-3">
            {["Milano", "Brescia", "Verona", "Bergamo", "Mantova", "Padova", "Vicenza", "Roma", "Torino", "Bologna", "Firenze", "Napoli"]
              .filter((c) => c.toLowerCase() !== slug)
              .map((c) => (
                <Link
                  key={c}
                  to={`/trasporti/${c.toLowerCase()}`}
                  className="px-4 py-2 border border-zinc-200 text-sm font-body text-zinc-600 hover:border-[#FF5200] hover:text-[#FF5200] transition-colors"
                >
                  Trasporti {c}
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
